import { useState, useRef, useEffect, useMemo } from 'react'
import { Search, X, FileText, LayoutDashboard, User, ChevronRight, Loader2, Zap, MessageSquare } from 'lucide-react'
import { useGlobalSearch } from '../hooks/useGlobalSearch'

const PRIORITY_COLORS = {
    highest: '#ef4444',
    high: '#f97316',
    medium: '#eab308',
    low: '#22c55e',
    lowest: '#6b7280',
}

export default function GlobalSearch({ companyId, onSelectCard, onSelectBoard, onSelectSuggestion, onSelectMember, isOpen, onClose, actions = [] }) {
    const [query, setQuery] = useState('')
    const { results, loading, search, clear, hasResults } = useGlobalSearch(companyId)
    const inputRef = useRef(null)
    const panelRef = useRef(null)

    // Filter actions by query (substring match on label)
    const matchedActions = useMemo(() => {
        if (!query || query.length < 1) return actions
        const q = query.toLowerCase()
        return actions.filter((a) => a.label.toLowerCase().includes(q))
    }, [actions, query])

    const runAction = (action) => {
        onClose()
        // Run after close so any subsequent UI (nav, modal) isn't blocked by palette
        setTimeout(() => action.run(), 0)
    }

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus()
        }
        if (!isOpen) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- reset query state when modal closes is a legit UX side-effect
            setQuery('')
            clear()
        }
    }, [isOpen, clear])

    // Close on Escape
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape' && isOpen) onClose()
        }
        document.addEventListener('keydown', handleKey)
        return () => document.removeEventListener('keydown', handleKey)
    }, [isOpen, onClose])

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (isOpen && panelRef.current && !panelRef.current.contains(e.target)) {
                onClose()
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen, onClose])

    const handleChange = (e) => {
        const val = e.target.value
        setQuery(val)
        search(val)
    }

    const handleSelectCard = (card) => {
        onSelectCard?.(card.id, card.board_id)
        onClose()
    }

    const handleSelectBoard = (boardId) => {
        onSelectBoard?.(boardId)
        onClose()
    }

    if (!isOpen) return null

    return (
        <div className="gsearch-overlay">
            <div className="gsearch-panel" ref={panelRef}>
                <div className="gsearch-input-wrap">
                    <Search size={18} className="gsearch-icon" />
                    <input
                        ref={inputRef}
                        className="gsearch-input"
                        placeholder="Cauta card, board sau persoana..."
                        value={query}
                        onChange={handleChange}
                        autoFocus
                    />
                    {loading && <Loader2 size={16} className="gsearch-spinner" />}
                    {query && (
                        <button className="gsearch-clear" onClick={() => { setQuery(''); clear() }}>
                            <X size={14} />
                        </button>
                    )}
                </div>

                {/* ACTIONS: shown when no query OR query matches some action label */}
                {matchedActions.length > 0 && (
                    <div className="gsearch-results">
                        <div className="gsearch-section">
                            <div className="gsearch-section-title">
                                <Zap size={14} /> Acțiuni
                            </div>
                            {matchedActions.map((action) => {
                                const Icon = action.icon
                                return (
                                    <button
                                        key={action.id}
                                        className="gsearch-item gsearch-item-action"
                                        onClick={() => runAction(action)}
                                    >
                                        {Icon ? <Icon size={14} className="gsearch-action-icon" /> : null}
                                        <span className="gsearch-item-text">{action.label}</span>
                                        {action.shortcut && (
                                            <span className="gsearch-action-shortcut">{action.shortcut}</span>
                                        )}
                                        <ChevronRight size={14} className="gsearch-chevron" />
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                )}

                {query.length >= 2 && (
                    <div className="gsearch-results">
                        {!loading && !hasResults && matchedActions.length === 0 && (
                            <div className="gsearch-empty">Niciun rezultat pentru &quot;{query}&quot;</div>
                        )}

                        {/* BOARDS */}
                        {results.boards.length > 0 && (
                            <div className="gsearch-section">
                                <div className="gsearch-section-title">
                                    <LayoutDashboard size={14} /> Board-uri
                                </div>
                                {results.boards.map((board) => (
                                    <button
                                        key={board.id}
                                        className="gsearch-item gsearch-item-board"
                                        onClick={() => handleSelectBoard(board.id)}
                                    >
                                        <span
                                            className="gsearch-board-dot"
                                            style={{ background: board.background_color || '#6366f1' }}
                                        />
                                        <span className="gsearch-item-text">{board.name}</span>
                                        <ChevronRight size={14} className="gsearch-chevron" />
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* MEMBERS */}
                        {results.members.length > 0 && (
                            <div className="gsearch-section">
                                <div className="gsearch-section-title">
                                    <User size={14} /> Persoane
                                </div>
                                {results.members.map((member) => (
                                    <div key={member.id} className="gsearch-item gsearch-item-member">
                                        <div
                                            className={`gsearch-member-header${onSelectMember ? ' gsearch-member-header-clickable' : ''}`}
                                            onClick={onSelectMember ? () => onSelectMember(member) : undefined}
                                            role={onSelectMember ? 'button' : undefined}
                                            tabIndex={onSelectMember ? 0 : undefined}
                                            onKeyDown={onSelectMember ? (e) => { if (e.key === 'Enter' || e.key === ' ') onSelectMember(member) } : undefined}
                                            title={onSelectMember ? 'Vezi board-ul angajatului (doar citire)' : undefined}
                                        >
                                            {member.avatar_url ? (
                                                <img src={member.avatar_url} alt="" className="gsearch-member-avatar" />
                                            ) : (
                                                <div className="gsearch-member-avatar gsearch-member-avatar-placeholder">
                                                    {(member.full_name || '?')[0]}
                                                </div>
                                            )}
                                            <div className="gsearch-member-info">
                                                <span className="gsearch-member-name">{member.full_name}</span>
                                                <span className="gsearch-member-role">
                                                    {member.position || member.role} · {member.total_tasks} task-uri
                                                    {onSelectMember ? ' · vezi board-ul →' : ''}
                                                </span>
                                            </div>
                                        </div>
                                        {member.board_counts.length > 0 && (
                                            <div className="gsearch-member-boards">
                                                {member.board_counts.map((bc) => (
                                                    <button
                                                        key={bc.board_id}
                                                        className="gsearch-member-board-row"
                                                        onClick={() => handleSelectBoard(bc.board_id)}
                                                    >
                                                        <span className="gsearch-member-board-name">{bc.board_name}</span>
                                                        <span className="gsearch-member-board-count">{bc.count}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* SUGGESTIONS (cross-tenant) */}
                        {results.suggestions?.length > 0 && (
                            <div className="gsearch-section">
                                <div className="gsearch-section-title">
                                    <MessageSquare size={14} /> Sugestii
                                </div>
                                {results.suggestions.map((s) => (
                                    <button
                                        key={s.id}
                                        className="gsearch-item gsearch-item-card"
                                        onClick={() => { onSelectSuggestion?.(s.id); onClose() }}
                                    >
                                        <div className="gsearch-card-main">
                                            <span className="gsearch-card-title">{s.title}</span>
                                        </div>
                                        <span className="gsearch-card-meta">
                                            {s.category} · {s.status} · {s.author_name}
                                            {s.author_company_name ? ` (${s.author_company_name})` : ''}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* CARDS */}
                        {results.cards.length > 0 && (
                            <div className="gsearch-section">
                                <div className="gsearch-section-title">
                                    <FileText size={14} /> Card-uri
                                </div>
                                {results.cards.map((card) => (
                                    <button
                                        key={card.id}
                                        className="gsearch-item gsearch-item-card"
                                        onClick={() => handleSelectCard(card)}
                                    >
                                        <div className="gsearch-card-main">
                                            {card.priority && (
                                                <span
                                                    className="gsearch-card-priority"
                                                    style={{ background: PRIORITY_COLORS[card.priority] || '#6b7280' }}
                                                />
                                            )}
                                            <span className={`gsearch-card-title ${card.is_completed ? 'completed' : ''}`}>
                                                {card.title}
                                            </span>
                                        </div>
                                        <span className="gsearch-card-meta">
                                            {card.board_name} · {card.list_name}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {!query && matchedActions.length === 0 && (
                    <div className="gsearch-hint">
                        Scrie minim 2 caractere pentru a cauta in toate board-urile.
                    </div>
                )}
            </div>
        </div>
    )
}
